import { motion } from 'framer-motion';
import ProviderButton from './ProviderButton';
import SecurityBadge from './SecurityBadge';
import TermsDisclaimer from './TermsDisclaimer';
import EmailDisplay from './EmailDisplay';
import SecurityInfo from './SecurityInfo';

interface ProviderSelectionProps {
  email: string;
  suggestedProvider: string;
  isSubmitting: boolean;
  error: string;
  setError: (error: string) => void;
  onProviderConnect: (provider: string) => void;
  onBack: () => void;
}

const ProviderSelection = ({
  email,
  suggestedProvider,
  isSubmitting,
  error,
  setError,
  onProviderConnect,
  onBack
}: ProviderSelectionProps) => {
  return (
    <motion.div
      key="provider-selection"
      layout
      initial={{ 
        opacity: 0, 
        y: 30
      }}
      animate={{ 
        opacity: 1, 
        y: 0,
        transition: { 
          type: "spring", 
          stiffness: 300, 
          damping: 25 
        }
      }}
      exit={{ 
        opacity: 0, 
        y: 30,
        transition: { 
          duration: 0.3,
          ease: [0.4, 0.0, 0.2, 1]
        } 
      }}
      className="px-8 pb-8"
    >
      {/* Email display with back button */}
      <EmailDisplay email={email} onBack={onBack} isSubmitting={isSubmitting} />
      
      {/* Security information text */}
      <SecurityInfo />
      
      {/* Provider selection */}
      <motion.div 
        layout
        transition={{
          layout: { type: "spring", stiffness: 300, damping: 30 }
        }}
        className="space-y-4"
      >
        {/* Google */}
        {suggestedProvider === 'Google' && (
          <ProviderButton 
            provider="Google"
            icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            }
            onClick={() => onProviderConnect('Google')}
            isSubmitting={isSubmitting}
            delay={0.3}
          />
        )}
        
        {/* Microsoft */}
        {suggestedProvider === 'Microsoft' && (
          <ProviderButton 
            provider="Microsoft"
            icon={
              <svg width="20" height="20" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 11H0V0H11V11Z" fill="#F25022"/>
                <path d="M23 11H12V0H23V11Z" fill="#7FBA00"/>
                <path d="M11 23H0V12H11V23Z" fill="#00A4EF"/>
                <path d="M23 23H12V12H23V23Z" fill="#FFB900"/>
              </svg>
            }
            onClick={() => onProviderConnect('Microsoft')}
            isSubmitting={isSubmitting}
            delay={0.3}
          />
        )}
        
        {/* Generic email provider */}
        {suggestedProvider === 'Email' && (
          <ProviderButton 
            provider="Email"
            icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#694e42]">
                <path d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z" fill="currentColor"/>
              </svg>
            }
            onClick={() => setError('Custom email provider integration is coming soon!')}
            isSubmitting={isSubmitting}
            delay={0.3}
          />
        )}
      </motion.div>
      
      {/* Error message */}
      {error && (
        <motion.div 
          className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-center text-red-500"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <p className="text-sm">{error}</p>
        </motion.div>
      )}
      
      <SecurityBadge />
      <TermsDisclaimer />
    </motion.div>
  );
};

export default ProviderSelection;